import {
  Company_Candidate_Type,
  Competency_Map_Status,
  Examination_Assessment_Type,
  Examination_Scoring_Type,
  Examination_Type,
  Grade,
  Language,
  Ml_Log_Status,
  Question_Type,
  Trial_Status,
} from "@generated/graphql";
import { generateOptions } from "helpers/index";
import palette from "theme/palette";

const GRADE_OPTIONS = [
  {
    id: Grade.Junior,
    displayName: "Junior",
  },
  {
    id: Grade.Middle,
    displayName: "Middle",
  },
  {
    id: Grade.Senior,
    displayName: "Senior",
  },
];

const QUESTION_TYPES = [
  {
    id: Question_Type.Practical,
    displayName: "Практический",
  },
  {
    id: Question_Type.Theoretical,
    displayName: "Теоретический",
  },
  {
    id: Question_Type.MultipleChoiceQuestions,
    displayName: "Тестовый",
  },
];

const QUESTION_TYPE_TITLE = {
  [Question_Type.Practical]: "Практика",
  [Question_Type.Theoretical]: "Теория",
  [Question_Type.MultipleChoiceQuestions]: "Тест",
};

const QUESTION_TYPE_TITLE_ADJECTIVE = {
  [Question_Type.Practical]: "Практический",
  [Question_Type.Theoretical]: "Теоретический",
  [Question_Type.MultipleChoiceQuestions]: "Тестовый",
};

const COMPETENCY_MAP_STATUSES = [
  {
    label: "Архив",
    value: Competency_Map_Status.Archive,
    badgeColor: "info" as const,
  },
  {
    label: "Драфт",
    value: Competency_Map_Status.Draft,
    badgeColor: "error" as const,
  },
  {
    label: "Подтверждено",
    value: Competency_Map_Status.Approved,
    badgeColor: "success" as const,
  },
  {
    label: "Генерация",
    value: Competency_Map_Status.Generating,
    badgeColor: "info" as const,
  },
];

const LANGUAGE_OPTIONS = [
  {
    id: Language.En,
    displayName: "Английский",
  },
  {
    id: Language.Kk,
    displayName: "Казахский",
  },
  {
    id: Language.Ru,
    displayName: "Русский",
  },
  {
    id: Language.Uz,
    displayName: "Узбекский",
  },
];

const COMPETENCY_MAP_STATUS_TITLE = {
  [Competency_Map_Status.Draft]: "Драфт",
  [Competency_Map_Status.Approved]: "Подтверждено",
  [Competency_Map_Status.Generating]: "Генерируется",
  [Competency_Map_Status.Virtual]: "Виртуальная",
  [Competency_Map_Status.Archive]: "Архивная",
};

const COMPETENCY_MAP_STATUS_OPTIONS = generateOptions(COMPETENCY_MAP_STATUS_TITLE);

// eslint-disable-next-line @typescript-eslint/naming-convention
enum Competency_Map_Tabs_Values {
  constructor = "constructor",
  documents = "documents",
  testingList = "testingList",
  questionsList = "questionsList",
}

const ML_LOG_STATUS_TITLE = {
  [Ml_Log_Status.Initialized]: "Генерируется",
  [Ml_Log_Status.Success]: "Успешно",
  [Ml_Log_Status.Error]: "Ошибка",
  [Ml_Log_Status.InternalError]: "Ошибка",
  [Ml_Log_Status.RequestFailed]: "Ошибка",
};

const EXAMINATION_TYPES_DATA = [
  {
    title: "Персональное тестирование",
    description: "Персональное единичное тестирование на конкретного кандидата.",
    type: Examination_Type.Personal,
    color: palette.testing.personal,
  },
  {
    title: "Групповое тестирование",
    description:
      "Тестирование на конкретную группу кандидатов. Тестирование будет отображаться в общем списке единой группы.",
    type: Examination_Type.Group,
    color: palette.testing.private,
  },
  {
    title: "Открытое тестирование",
    description: "Обезличенное тестирование. Кандидаты могут пройти по ссылке и стать участниками тестирования.",
    type: Examination_Type.Open,
    color: palette.testing.open,
  },
  {
    title: "Входное тестирование",
    description:
      // eslint-disable-next-line max-len
      "Тестирование, не требующее предварительной регистрации. Кандидат регистрируется непосредственно перед прохождением тестирования",
    type: Examination_Type.Public,
    color: palette.testing.public,
  },
];

const EXAMINATION_TYPE_TITLE = {
  [Examination_Type.Open]: "Открытое",
  [Examination_Type.Group]: "Групповое",
  [Examination_Type.Personal]: "Персональное",
  [Examination_Type.Public]: "Входное",
};

const ASSESSMENT_TYPE_TITLE = {
  [Examination_Assessment_Type.Advanced]: "Расширенный",
  [Examination_Assessment_Type.Assessment]: "Ассесмент",
  [Examination_Assessment_Type.Screening]: "Скрининг",
};

const SCORING_TYPE_TITLE = {
  [Examination_Scoring_Type.GradeDetermination]: "Определение грейда",
  [Examination_Scoring_Type.PassingThreshold]: "Порог прохождения",
};

const TRIAL_STATUS_TITLE = {
  [Trial_Status.Canceled]: "Отменено",
  [Trial_Status.Created]: "Создано",
  [Trial_Status.Received]: "Получено",
  [Trial_Status.Started]: "Запущено",
  [Trial_Status.Completed]: "Завершено",
  [Trial_Status.Suspended]: "Приостановлено",
};

const GENERATE_QUESTIONS_DEFAULT_AMOUNT = 2;

const COMPANY_CANDIDATE_TYPE_TITLE: Record<Company_Candidate_Type, string> = {
  [Company_Candidate_Type.Candidate]: "Кандидат",
  [Company_Candidate_Type.Employee]: "Сотрудник",
};
const CANDIDATE_TYPE_OPTIONS = [
  {
    id: Company_Candidate_Type.Candidate,
    displayName: "Кандидат",
  },
  {
    id: Company_Candidate_Type.Employee,
    displayName: "Сотрудник",
  },
];

const CANDIDATE_IDS_QUERY_NAME = "candidateIds";

const QUESTIONS_TABS_VALUES = {
  confirmed: "confirmed",
  unconfirmed: "unconfirmed",
};

const COMPETENCIES_IDS_PARAM_NAME = "competentiesIds";

const KNOWLEDGE_BLOCKS_IDS_PARAM_NAME = "knowledgeBlockIds";

enum GraphQLErrorCodes {
  BAD_USER_INPUT = "BAD_USER_INPUT",
  INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR",
}

const HEADER_HEIGHT = "60px";

export {
  ASSESSMENT_TYPE_TITLE,
  CANDIDATE_IDS_QUERY_NAME,
  CANDIDATE_TYPE_OPTIONS,
  COMPANY_CANDIDATE_TYPE_TITLE,
  COMPETENCIES_IDS_PARAM_NAME,
  COMPETENCY_MAP_STATUS_OPTIONS,
  COMPETENCY_MAP_STATUS_TITLE,
  COMPETENCY_MAP_STATUSES,
  Competency_Map_Tabs_Values,
  EXAMINATION_TYPE_TITLE,
  GENERATE_QUESTIONS_DEFAULT_AMOUNT,
  GRADE_OPTIONS,
  GraphQLErrorCodes,
  HEADER_HEIGHT,
  KNOWLEDGE_BLOCKS_IDS_PARAM_NAME,
  LANGUAGE_OPTIONS,
  ML_LOG_STATUS_TITLE,
  QUESTION_TYPE_TITLE,
  QUESTION_TYPE_TITLE_ADJECTIVE,
  QUESTION_TYPES,
  QUESTIONS_TABS_VALUES,
  SCORING_TYPE_TITLE,
  EXAMINATION_TYPES_DATA as TESTING_TYPES_DATA,
  TRIAL_STATUS_TITLE,
};
